import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { Message, Chat } from "../../types/conversations";
import conversationsService from "../../services/conversations";
import { ChatList } from "../../components/conversations/ChatList";
import { ChatHeader } from "../../components/conversations/ChatHeader/index";
import { MessagesList } from "../../components/conversations/MessagesList";
import { MessageInput } from "../../components/conversations/MessageInput";
import { EmptyChatState } from "../../components/conversations/EmptyChatState";
import { SupportChatView } from "../../components/conversations/SupportChatView";
import { useAuth } from "../../context/AuthContext";
import StudentManagementModal from "../../components/students/StudentManagementModal/StudentManagementModal";
import { User } from "../../types";
import { useMobileDetection } from "../../components/profile/onboarding/hooks/useMobileDetection";
import { useConversations } from "../../context/ConversationsContext";

const SUPPORT_CHAT_ID = -1;

const SUPPORT_CHAT: Chat = {
  id: SUPPORT_CHAT_ID,
  name: "Suporte Easyfit",
  avatar: null,
  userId: null,
  lastMessage: "Fale com nossa equipe de suporte",
  lastMessageTime: "",
  unreadCount: 0,
  online: true,
  lastSeen: null,
  isStudent: false,
};

// Função helper para throttle
function throttle(func: () => void, wait: number): () => void {
  let timeout: NodeJS.Timeout | null = null;
  let previous = 0;

  return function () {
    const now = Date.now();
    const remaining = wait - (now - previous);

    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      func();
    } else if (!timeout) {
      timeout = setTimeout(() => {
        previous = Date.now();
        timeout = null;
        func();
      }, remaining);
    }
  };
}

const Conversations: React.FC = () => {
  const { getUser } = useAuth();
  const [chats, setChats] = useState<Chat[]>([]);
  const [selectedChat, setSelectedChat] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
  const [messageInput, setMessageInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<User | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [chatsOffset, setChatsOffset] = useState(0);
  const [hasMoreChats, setHasMoreChats] = useState(true);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatsContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollTop = useRef(0);
  const isInitialLoad = useRef(true);
  const messagesRef = useRef<Message[]>([]);
  const displayedMessagesRef = useRef<Message[]>([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const scrollRestoreRef = useRef<{
    previousScrollHeight: number;
    previousScrollTop: number;
  } | null>(null);

  const MESSAGES_PER_PAGE = 15;
  const CHATS_PER_PAGE = 20;

  // --vh: fallback para browsers sem 100dvh (desktop e Android antigo)
  useEffect(() => {
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setViewportHeight();
    window.addEventListener("resize", setViewportHeight);
    window.addEventListener("orientationchange", setViewportHeight);
    return () => {
      window.removeEventListener("resize", setViewportHeight);
      window.removeEventListener("orientationchange", setViewportHeight);
    };
  }, []);

  // Carrega chats com paginação
  const loadChats = useCallback(
    async (offset: number = 0, append: boolean = false) => {
      if (append) {
        setIsLoadingMoreChats(true);
      } else {
        setLoading(true);
      }
      try {
        const result = await conversationsService.getAllChats(
          CHATS_PER_PAGE,
          offset,
        );
        if (append) {
          // Adiciona novos chats ao final da lista
          setChats((prev) => {
            const existingIds = new Set(prev.map((c) => c.id));
            const newChats = result.chats.filter((c) => !existingIds.has(c.id));
            return [...prev, ...newChats];
          });
          setChatsOffset(result.offset + result.chats.length);
        } else {
          // Substitui todos os chats
          setChats(result.chats);
          setChatsOffset(result.chats.length);
        }
        setHasMoreChats(result.has_more);
      } catch (error) {
        console.error("Erro ao carregar chats:", error);
      } finally {
        if (append) {
          setIsLoadingMoreChats(false);
        } else {
          setLoading(false);
        }
      }
    },
    [CHATS_PER_PAGE],
  );

  useEffect(() => {
    setChatsOffset(0);
    setHasMoreChats(true);
    loadChats(0, false);
  }, [loadChats]);

  // Ref para armazenar o último handler de scroll de chats
  const handleChatsScrollRef = useRef<() => void>();

  // Scroll infinito para chats - melhorado com throttle e melhor detecção
  const handleChatsScrollInternal = useCallback(() => {
    const container = chatsContainerRef.current;
    if (
      !container ||
      isLoadingMoreChats ||
      !hasMoreChats ||
      loading ||
      searchQuery.trim() !== ""
    )
      return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Carregar mais chats quando estiver próximo do final (300px do fim para carregar antes)
    // Isso torna o carregamento mais suave e prevê o scroll
    if (distanceFromBottom < 300 && distanceFromBottom > 0) {
      setIsLoadingMoreChats(true);
      loadChats(chatsOffset, true).then(() => {
        setIsLoadingMoreChats(false);
      });
    }
  }, [
    chatsOffset,
    isLoadingMoreChats,
    hasMoreChats,
    loading,
    searchQuery,
    loadChats,
  ]);

  // Atualizar ref quando o handler mudar
  useEffect(() => {
    handleChatsScrollRef.current = handleChatsScrollInternal;
  }, [handleChatsScrollInternal]);

  // Criar versão throttled que sempre chama a versão mais recente
  const handleChatsScroll = useMemo(
    () =>
      throttle(() => {
        if (handleChatsScrollRef.current) {
          handleChatsScrollRef.current();
        }
      }, 150),
    [],
  );

  // Carrega mensagens quando seleciona um chat (primeira página - mensagens mais novas)
  const loadMessages = useCallback(
    async (chatId: number, offset: number = 0) => {
      const isInitial = offset === 0;
      if (isInitial) {
        setMessagesLoading(true);
      }
      try {
        const result = await conversationsService.getMessagesByChatId(
          chatId,
          MESSAGES_PER_PAGE,
          offset,
        );

        // As mensagens vêm ordenadas por timestamp DESC (mais novas primeiro)
        // Precisamos inverter para exibir (mais antigas primeiro na tela)
        const orderedMessages = [...result.messages].reverse();

        if (offset === 0) {
          // Primeira carga: substitui todas as mensagens
          setMessages(orderedMessages);
          messagesRef.current = orderedMessages;
          setDisplayedMessages(orderedMessages);
          displayedMessagesRef.current = orderedMessages;
          setCurrentOffset(result.messages.length);
        } else {
          // Carregando mais mensagens antigas: adiciona no início
          const container = messagesContainerRef.current;
          if (container) {
            scrollRestoreRef.current = {
              previousScrollHeight: container.scrollHeight,
              previousScrollTop: container.scrollTop,
            };
          }

          setMessages((prev) => {
            const combined = [...orderedMessages, ...prev];
            const unique = combined.filter(
              (msg, index, self) =>
                index ===
                self.findIndex((m) => Number(m.id) === Number(msg.id)),
            );
            return unique.sort(
              (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
            );
          });
          setDisplayedMessages((prev) => {
            const combined = [...orderedMessages, ...prev];
            const unique = combined.filter(
              (msg, index, self) =>
                index ===
                self.findIndex((m) => Number(m.id) === Number(msg.id)),
            );
            return unique.sort(
              (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
            );
          });
          setCurrentOffset((prev) => prev + result.messages.length);
        }

        setHasMore(result.has_more);
        isInitialLoad.current = offset === 0;
        setHasLoadedOnce(true);
      } catch (error) {
        console.error("Erro ao carregar mensagens:", error);
      } finally {
        if (isInitial) {
          setMessagesLoading(false);
        }
      }
    },
    [MESSAGES_PER_PAGE],
  );

  useEffect(() => {
    if (selectedChat) {
      setCurrentOffset(0);
      setHasMore(true);
      isLoadingMoreRef.current = false;
      scrollRestoreRef.current = null;
      loadMessages(selectedChat, 0);
    }
  }, [selectedChat, loadMessages]);

  const handleRefresh = useCallback(async () => {
    setRefreshKey((prev) => prev + 1);
    if (searchQuery.trim()) {
      setLoading(true);
      try {
        const data = await conversationsService.getChatsBySearch(searchQuery);
        setChats(data);
      } catch (error) {
        console.error("Erro ao buscar chats:", error);
      } finally {
        setLoading(false);
      }
    } else {
      await loadChats();
    }

    if (selectedChat) {
      setCurrentOffset(0);
      setHasMore(true);
      await loadMessages(selectedChat, 0);
    }

    // Recarregar chats quando não há busca
    if (!searchQuery.trim()) {
      setChatsOffset(0);
      setHasMoreChats(true);
      await loadChats(0, false);
    }
  }, [loadChats, loadMessages, selectedChat, searchQuery]);

  // Atualização automática a cada 1 minuto após o primeiro carregamento
  useEffect(() => {
    if (!hasLoadedOnce) return;

    const updateData = async () => {
      try {
        // Atualizar lista de conversas
        const updatedChatsResult = await conversationsService.getAllChats(
          CHATS_PER_PAGE,
          0,
        );
        setChats(updatedChatsResult.chats);
        setChatsOffset(updatedChatsResult.chats.length);
        setHasMoreChats(updatedChatsResult.has_more);

        // Atualizar mensagens do chat atual se houver um selecionado
        if (selectedChat) {
          // Buscar apenas as mensagens mais novas (primeira página)
          const result = await conversationsService.getMessagesByChatId(
            selectedChat,
            MESSAGES_PER_PAGE,
            0,
          );

          // Inverter ordem para exibição (mais antigas primeiro)
          const orderedNewMessages = [...result.messages].reverse();

          // Verificar se há novas mensagens antes de atualizar
          const currentLastMessageId =
            messagesRef.current.length > 0
              ? Number(messagesRef.current[messagesRef.current.length - 1]?.id)
              : null;
          const newLastMessageId =
            orderedNewMessages.length > 0
              ? Number(orderedNewMessages[orderedNewMessages.length - 1]?.id)
              : null;

          // Se houver novas mensagens, adicionar ao final da lista
          if (newLastMessageId && newLastMessageId !== currentLastMessageId) {
            const container = messagesContainerRef.current;
            const isNearBottom = container
              ? container.scrollHeight -
                  container.scrollTop -
                  container.clientHeight <
                100
              : false;

            // Encontrar apenas as mensagens realmente novas
            const existingIds = new Set(
              messagesRef.current.map((m) => Number(m.id)),
            );
            const trulyNewMessages = orderedNewMessages.filter(
              (m) => !existingIds.has(Number(m.id)),
            );

            if (trulyNewMessages.length > 0) {
              setMessages((prev) => {
                const combined = [...prev, ...trulyNewMessages];
                // Remover duplicatas e ordenar
                const unique = combined.filter(
                  (msg, index, self) =>
                    index ===
                    self.findIndex((m) => Number(m.id) === Number(msg.id)),
                );
                return unique.sort(
                  (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
                );
              });

              if (isNearBottom) {
                // Usuário está no final, adicionar novas mensagens e fazer scroll
                setDisplayedMessages((prev) => {
                  const combined = [...prev, ...trulyNewMessages];
                  const unique = combined.filter(
                    (msg, index, self) =>
                      index ===
                      self.findIndex((m) => Number(m.id) === Number(msg.id)),
                  );
                  return unique.sort(
                    (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
                  );
                });
                requestAnimationFrame(() => {
                  requestAnimationFrame(() => {
                    messagesEndRef.current?.scrollIntoView({
                      behavior: "smooth",
                    });
                  });
                });
              }
            }
          }
        }
      } catch (error) {
        console.error("Erro ao atualizar dados:", error);
      }
    };

    // Executar imediatamente após o primeiro carregamento
    const intervalId = setInterval(updateData, 60000); // 1 minuto = 60000ms

    // Limpar intervalo quando o componente desmontar ou quando o chat selecionado mudar
    return () => {
      clearInterval(intervalId);
    };
  }, [hasLoadedOnce, selectedChat, MESSAGES_PER_PAGE]);

  // Sincronizar refs com os estados
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    displayedMessagesRef.current = displayedMessages;
  }, [displayedMessages]);

  // Restaurar posição do scroll após carregar mais mensagens (quando o spinner some)
  useEffect(() => {
    if (!isLoadingMore && scrollRestoreRef.current) {
      const container = messagesContainerRef.current;
      const saved = scrollRestoreRef.current;
      scrollRestoreRef.current = null;
      if (container && saved.previousScrollHeight > 0) {
        requestAnimationFrame(() => {
          const newHeight = container.scrollHeight;
          const diff = newHeight - saved.previousScrollHeight;
          if (diff > 0) {
            container.scrollTop = saved.previousScrollTop + diff;
          }
        });
      }
    }
  }, [displayedMessages, isLoadingMore]);

  // Scroll automático para última mensagem
  useEffect(() => {
    if (isInitialLoad.current && displayedMessages.length > 0) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
          isInitialLoad.current = false;
        });
      });
    }
  }, [displayedMessages]);

  // Ref para armazenar o último handler de scroll de mensagens
  const handleScrollRef = useRef<() => void>();

  // Infinite scroll - carrega mensagens mais antigas ao rolar para cima (melhorado)
  const handleScrollInternal = useCallback(() => {
    const container = messagesContainerRef.current;
    if (
      !container ||
      isLoadingMoreRef.current ||
      isLoadingMore ||
      !hasMore ||
      !selectedChat ||
      messagesLoading
    )
      return;

    const scrollTop = container.scrollTop;
    const distanceFromTop = scrollTop;

    // Carregar mais mensagens quando estiver próximo do topo (300px do topo para carregar antes)
    if (distanceFromTop < 300 && distanceFromTop >= 0) {
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
      loadMessages(selectedChat, currentOffset)
        .then(() => {
          setIsLoadingMore(false);
        })
        .finally(() => {
          isLoadingMoreRef.current = false;
        });
    }

    lastScrollTop.current = scrollTop;
  }, [
    selectedChat,
    currentOffset,
    isLoadingMore,
    hasMore,
    messagesLoading,
    loadMessages,
  ]);

  // Atualizar ref quando o handler mudar
  useEffect(() => {
    handleScrollRef.current = handleScrollInternal;
  }, [handleScrollInternal]);

  // Criar versão throttled que sempre chama a versão mais recente
  const handleScroll = useMemo(
    () =>
      throttle(() => {
        if (handleScrollRef.current) {
          handleScrollRef.current();
        }
      }, 100),
    [],
  );

  // Handle paste (Ctrl+V)
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) files.push(file);
        }
      }

      if (files.length > 0) {
        e.preventDefault();
        setAttachedFiles((prev) => [...prev, ...files]);
      }
    };

    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, []);

  const handleSendMessage = async () => {
    if (!selectedChat || (!messageInput.trim() && attachedFiles.length === 0))
      return;

    const user = getUser();
    if (!user?.id) return;

    // Envia arquivos
    for (const file of attachedFiles) {
      const isImage = file.type.startsWith("image/");

      try {
        // Upload do arquivo
        const uploadResult = await conversationsService.uploadMessageFile(file);

        if (!uploadResult) {
          console.error("Falha ao fazer upload do arquivo:", file.name);
          continue;
        }

        // Criar mensagem com URL do arquivo
        const message: Omit<Message, "id" | "timestamp"> = {
          chatId: selectedChat,
          senderId: user.id,
          content: isImage ? "Imagem enviada" : uploadResult.originalName,
          type: isImage ? "image" : "file",
          fileUrl: uploadResult.url,
          fileName: uploadResult.originalName,
          fileSize: `${Math.round(file.size / 1024)} KB`,
          status: "sent",
        };

        const savedMessage = await conversationsService.addMessageToChat(
          selectedChat,
          message,
        );
        if (savedMessage) {
          // Garantir que o timestamp é um Date válido
          const messageWithValidTimestamp = {
            ...savedMessage,
            timestamp:
              savedMessage.timestamp instanceof Date
                ? savedMessage.timestamp
                : new Date(savedMessage.timestamp),
          };

          // Verificar se a data é válida antes de adicionar
          if (!isNaN(messageWithValidTimestamp.timestamp.getTime())) {
            // Normalizar ID para número para comparação correta
            const messageId = Number(messageWithValidTimestamp.id);

            setMessages((prev) => {
              // Verificar se a mensagem já existe (evitar duplicatas) - comparar como números
              if (prev.some((m) => Number(m.id) === messageId)) {
                return prev;
              }
              const updated = [...prev, messageWithValidTimestamp];
              // Ordenar por timestamp
              return updated.sort(
                (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
              );
            });
            setDisplayedMessages((prev) => {
              // Verificar se a mensagem já existe (evitar duplicatas) - comparar como números
              if (prev.some((m) => Number(m.id) === messageId)) {
                return prev;
              }
              const updated = [...prev, messageWithValidTimestamp];
              // Ordenar por timestamp
              return updated.sort(
                (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
              );
            });
            // Scroll para última mensagem
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
              });
            });
          } else {
            console.error("Mensagem com timestamp inválido:", savedMessage);
          }
        }
      } catch (error) {
        console.error("Erro ao enviar arquivo:", error);
        // Continuar com o próximo arquivo mesmo se houver erro
      }
    }

    // Envia texto
    if (messageInput.trim()) {
      const message: Omit<Message, "id" | "timestamp"> = {
        chatId: selectedChat,
        senderId: user.id,
        content: messageInput,
        type: "text",
        status: "sent",
      };

      const savedMessage = await conversationsService.addMessageToChat(
        selectedChat,
        message,
      );
      console.log("Mensagem retornada do serviço:", savedMessage);
      if (!savedMessage) {
        // Se não retornou mensagem (null), significa que o backend retornou array antigo
        // Recarregar mensagens do servidor após um delay maior para garantir que foi salvo
        console.log(
          "Mensagem não retornada, recarregando mensagens do servidor...",
        );

        // Função para recarregar mensagens com retry
        const reloadMessages = async (attempt = 1, maxAttempts = 3) => {
          try {
            const result = await conversationsService.getMessagesByChatId(
              selectedChat,
              MESSAGES_PER_PAGE,
              0,
            );
            const orderedMessages = [...result.messages].reverse();
            setMessages(orderedMessages);
            setDisplayedMessages(orderedMessages);
            setCurrentOffset(result.messages.length);
            setHasMore(result.has_more);
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
              });
            });
          } catch (error) {
            console.error(
              `Erro ao recarregar mensagens (tentativa ${attempt}/${maxAttempts}):`,
              error,
            );
            // Se falhar e ainda tiver tentativas, tentar novamente após delay maior
            if (attempt < maxAttempts) {
              setTimeout(
                () => reloadMessages(attempt + 1, maxAttempts),
                500 * attempt,
              );
            }
          }
        };

        // Primeira tentativa após 500ms, depois retry se necessário
        setTimeout(() => reloadMessages(), 500);
        return;
      }

      // Garantir que o timestamp é um Date válido
      const messageWithValidTimestamp = {
        ...savedMessage,
        timestamp:
          savedMessage.timestamp instanceof Date
            ? savedMessage.timestamp
            : new Date(savedMessage.timestamp),
      };

      console.log(
        "Mensagem com timestamp processado:",
        messageWithValidTimestamp,
      );

      // Verificar se a data é válida antes de adicionar
      if (!isNaN(messageWithValidTimestamp.timestamp.getTime())) {
        console.log(
          "Adicionando mensagem aos estados. ID:",
          messageWithValidTimestamp.id,
          "Tipo:",
          typeof messageWithValidTimestamp.id,
        );

        // Normalizar ID para número para comparação correta
        const messageId = Number(messageWithValidTimestamp.id);

        setMessages((prev) => {
          // Verificar se a mensagem já existe (evitar duplicatas) - comparar como números
          const existingIndex = prev.findIndex(
            (m) => Number(m.id) === messageId,
          );
          console.log(
            "Verificando duplicatas no estado messages:",
            existingIndex !== -1,
            "Total de mensagens:",
            prev.length,
            "ID procurado:",
            messageId,
          );

          if (existingIndex !== -1) {
            // Se a mensagem já existe, atualizar ela ao invés de não adicionar
            console.log(
              "Mensagem já existe no índice",
              existingIndex,
              "- atualizando ao invés de ignorar",
            );
            const updated = [...prev];
            updated[existingIndex] = messageWithValidTimestamp;
            // Ordenar por timestamp
            const sorted = updated.sort(
              (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
            );
            console.log(
              "Mensagem atualizada no estado messages. Total:",
              sorted.length,
            );
            return sorted;
          }

          const updated = [...prev, messageWithValidTimestamp];
          // Ordenar por timestamp
          const sorted = updated.sort(
            (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
          );
          console.log(
            "Mensagem adicionada ao estado messages. Novo total:",
            sorted.length,
          );
          return sorted;
        });
        setDisplayedMessages((prev) => {
          // Verificar se a mensagem já existe (evitar duplicatas) - comparar como números
          const existingIndex = prev.findIndex(
            (m) => Number(m.id) === messageId,
          );
          console.log(
            "Verificando duplicatas no estado displayedMessages:",
            existingIndex !== -1,
            "Total de mensagens:",
            prev.length,
            "ID procurado:",
            messageId,
          );

          if (existingIndex !== -1) {
            // Se a mensagem já existe, atualizar ela ao invés de não adicionar
            console.log(
              "Mensagem já existe no displayedMessages no índice",
              existingIndex,
              "- atualizando ao invés de ignorar",
            );
            const updated = [...prev];
            updated[existingIndex] = messageWithValidTimestamp;
            // Ordenar por timestamp
            const sorted = updated.sort(
              (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
            );
            console.log(
              "Mensagem atualizada no displayedMessages. Total:",
              sorted.length,
            );
            return sorted;
          }

          const updated = [...prev, messageWithValidTimestamp];
          // Ordenar por timestamp
          const sorted = updated.sort(
            (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
          );
          console.log(
            "Mensagem adicionada ao displayedMessages. Novo total:",
            sorted.length,
          );
          return sorted;
        });
        // Scroll para última mensagem
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
          });
        });
      } else {
        console.error(
          "Mensagem com timestamp inválido:",
          savedMessage,
          "Timestamp value:",
          messageWithValidTimestamp.timestamp,
        );
      }
    }

    setMessageInput("");
    setAttachedFiles([]);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    });
  };

  const handleFileSelect = (files: File[]) => {
    setAttachedFiles((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSearch = async (query: string) => {
    setSearchQuery(query);
    if (query.trim()) {
      setLoading(true);
      try {
        const data = await conversationsService.getChatsBySearch(query);
        setChats(data);
        setHasMoreChats(false); // Busca não suporta paginação ainda
      } catch (error) {
        console.error("Erro ao buscar chats:", error);
      } finally {
        setLoading(false);
      }
    } else {
      setChatsOffset(0);
      setHasMoreChats(true);
      const result = await conversationsService.getAllChats(CHATS_PER_PAGE, 0);
      setChats(result.chats);
      setChatsOffset(result.chats.length);
      setHasMoreChats(result.has_more);
    }
  };

  const isSupportSelected = selectedChat === SUPPORT_CHAT_ID;

  function resolveSelectedChat() {
    if (isSupportSelected) return SUPPORT_CHAT;
    if (selectedChat) return chats.find((chat) => chat.id === selectedChat);
    return undefined;
  }
  const selectedChatData = resolveSelectedChat();

  const isChatSelected = !!selectedChatData;
  const isMobile = useMobileDetection();
  const { setIsChatSelected, isKeyboardOpen } = useConversations();

  useEffect(() => {
    setIsChatSelected(isChatSelected);
    return () => setIsChatSelected(false);
  }, [isChatSelected, setIsChatSelected]);

  // Intercepta o botão de voltar do Android/browser quando um chat está aberto
  useEffect(() => {
    if (!isMobile) return;

    if (isChatSelected) {
      // Empurra uma entrada no histórico para que o "voltar" possa ser interceptado
      window.history.pushState({ chatOpen: true }, "");
    }

    const handlePopState = (e: PopStateEvent) => {
      if (isChatSelected) {
        // Evita navegação para fora e fecha o chat
        e.preventDefault();
        setSelectedChat(null);
        // Garante que o estado do histórico fique correto
        window.history.pushState(null, "");
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isChatSelected, isMobile]);

  const handleStudentClick = async () => {
    if (!selectedChat) return;

    const student = await conversationsService.getChatStudent(selectedChat);
    if (student) {
      setSelectedStudent(student);
    }
  };

  // Desktop: desconta o header do app. Mobile: CSS via classes controla tudo.
  const desktopHeaderOffset = isChatSelected ? 0 : 65;
  const containerHeight = isMobile
    ? undefined
    : `calc(var(--vh, 1vh) * 100 - ${desktopHeaderOffset}px)`;

  return (
    <div
      className={`conversations-container conversations-no-bounce flex flex-col overflow-hidden bg-gray-100 dark:bg-gray-900 w-full md:flex-row min-h-0${isMobile ? " conversations-mobile" : ""}${isMobile && isChatSelected ? " chat-selected" : ""}${isMobile && isKeyboardOpen ? " keyboard-open" : ""}`}
      style={
        containerHeight
          ? { height: containerHeight, maxHeight: containerHeight, minHeight: 0 }
          : { height: "100%", maxHeight: "100%", minHeight: 0 }
      }
    >
      <div
        className={`conversations-chat-list w-full min-h-0 flex flex-col ${
          isChatSelected ? "hidden md:flex md:w-auto" : "flex md:w-auto"
        }`}
        style={{
          height: "100%",
          maxHeight: "100%",
          minHeight: 0,
        }}
      >
        <ChatList
          chats={chats}
          selectedChatId={selectedChat}
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          onChatSelect={setSelectedChat}
          loading={loading}
          onRefresh={handleRefresh}
          onScroll={handleChatsScroll}
          chatsContainerRef={chatsContainerRef}
          isLoadingMore={isLoadingMoreChats}
          viewerIsProfessional={getUser()?.is_professional ?? false}
          pinnedChat={SUPPORT_CHAT}
        />
      </div>
      <div
        className={`conversations-messages-area flex-1 min-h-0 min-w-0 bg-gray-50 dark:bg-gray-900 overflow-hidden flex flex-col relative ${
          isChatSelected ? "flex" : "hidden md:flex"
        }`}
        style={{
          maxHeight: "100%",
          minHeight: 0,
        }}
      >
        {isSupportSelected && (
          <SupportChatView onBack={() => setSelectedChat(null)} />
        )}
        {!isSupportSelected && selectedChatData && (
          <>
            <div className="flex-shrink-0">
              <ChatHeader
                chat={selectedChatData}
                onStudentClick={handleStudentClick}
                messages={messages}
                currentUserId={getUser()?.id}
                onBack={() => setSelectedChat(null)}
                isStudent={selectedChatData.isStudent}
                onLinkSuccess={() => loadChats(0, false)}
              />
            </div>

            <div className="conversations-messages-inner flex-1 min-h-0 flex flex-col overflow-hidden">
              {messagesLoading ? (
                <div
                  className="conversations-scroll flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2 md:px-4 md:py-4 md:space-y-3"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg width='40' height='40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0z' fill='%23f0f0f0' fill-opacity='0.05'/%3E%3C/svg%3E\")",
                  }}
                >
                  {[...Array(6)].map((_, i) => {
                    const isFromMe = i % 3 === 0;
                    return (
                      <div
                        key={i}
                        className={`flex ${
                          isFromMe ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-[85%] md:max-w-[70%] ${
                            isFromMe ? "order-2" : "order-1"
                          }`}
                        >
                          <div
                            className={`rounded-lg px-3 py-2 ${
                              isFromMe
                                ? "bg-purple-400"
                                : "bg-white dark:bg-gray-800"
                            }`}
                          >
                            <div
                              className={`h-4 ${
                                i % 2 === 0 ? "w-32 md:w-48" : "w-24 md:w-32"
                              } mb-2 rounded ${
                                isFromMe
                                  ? "bg-white/30"
                                  : "bg-gray-200 dark:bg-gray-700"
                              } animate-pulse`}
                            />
                            <div
                              className={`h-4 ${
                                i % 2 === 0 ? "w-24 md:w-32" : "w-32 md:w-40"
                              } rounded ${
                                isFromMe
                                  ? "bg-white/30"
                                  : "bg-gray-200 dark:bg-gray-700"
                              } animate-pulse`}
                            />
                            <div
                              className={`flex items-center gap-1 mt-1 ${
                                isFromMe ? "justify-end" : "justify-start"
                              }`}
                            >
                              <div
                                className={`h-2 w-12 rounded ${
                                  isFromMe
                                    ? "bg-white/30"
                                    : "bg-gray-200 dark:bg-gray-700"
                                } animate-pulse`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <MessagesList
                  messages={displayedMessages}
                  isLoadingMore={isLoadingMore}
                  onScroll={handleScroll}
                  messagesContainerRef={messagesContainerRef}
                  messagesEndRef={messagesEndRef}
                />
              )}

              <div className="flex-shrink-0">
                <MessageInput
                  value={messageInput}
                  attachedFiles={attachedFiles}
                  onValueChange={setMessageInput}
                  onSend={handleSendMessage}
                  onFileSelect={handleFileSelect}
                  onRemoveAttachment={removeAttachment}
                />
              </div>
            </div>
          </>
        )}
        {!isSupportSelected && !selectedChatData && (
          <EmptyChatState key={refreshKey} />
        )}
      </div>
      <StudentManagementModal
        isOpen={!!selectedStudent}
        onClose={() => setSelectedStudent(null)}
        student={selectedStudent}
      />
    </div>
  );
};

export default Conversations;
